import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getApiUrl } from "src/environment";
import { API_ERROR_CODES, type ApiError } from "src/interfaces/ApiError";
import { setShowGDPRModal } from "src/redux/slices/preferencesSlice";
import {
  getApiAuthData,
  getDateTimeRecord,
  mergeObjects,
  redirectToLogin,
} from "src/utils";
import { Notification } from "src/utils/Notification";

import { parseErrorMessage } from "../utils";

interface GetApiProps<T> {
  request: string;
  body?: Record<string, any>;
  mapper?: (data: any) => T;
  fetchOnInit?: boolean;
  cache?: boolean;
}

const parseErrorText = (value: unknown, fallback: string): string =>
  typeof value === "string" ? value : fallback;

const resolveApiError = (result: any, request: string): ApiError | null => {
  if (request === "get-logbook") {
    const content = result?.data?.content;
    if (result.success === false || content?.success === false) {
      return {
        code: API_ERROR_CODES.FORBIDDEN,
        message: parseErrorText(
          content?.error ?? result.error,
          "You are not authorized to view this entry.",
        ),
      };
    }
  }

  if (result.success === false && result.error) {
    return {
      code: API_ERROR_CODES.ERROR,
      message: parseErrorText(result.error, "Something went wrong."),
    };
  }

  if (result.error) {
    return {
      code: API_ERROR_CODES.ERROR,
      message: parseErrorText(result.error, "Something went wrong."),
    };
  }

  return null;
};

const shouldNotify = (error: ApiError) =>
  error.code !== API_ERROR_CODES.FORBIDDEN;

interface GetApiReturn<T> {
  data: T | undefined;
  setData: React.Dispatch<React.SetStateAction<T | undefined>>;
  setError: React.Dispatch<React.SetStateAction<ApiError | undefined>>;
  refetch: (
    body?: Record<string, any>,
    override?: boolean,
  ) => Promise<T | undefined>;
  isLoading: boolean;
  error?: ApiError;
}

/**
 * Custom hook to fetch data from an API with optional caching and data mapping.
 *
 * @template T - The type of the data being fetched.
 * @param request - The API request endpoint.
 * @param body - (Optional) The request body to send with the API call.
 * @param mapper - (Optional) A function to map the API response data to a specific format.
 * @param fetchOnInit - (Optional) A boolean indicating whether to fetch data on component mount. Defaults to true.
 * @param cache - (Optional) A boolean indicating whether to cache the API response. Defaults to true.
 * @returns An object containing the fetched data, a function to refetch the data, a loading state, and a setter for the data.
 */
export const useGetApi = <T>({
  request,
  body,
  mapper,
  fetchOnInit = true,
  cache = true,
}: GetApiProps<T>): GetApiReturn<T> => {
  const [data, setData] = useState<T>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError>();
  const dispatch = useDispatch();

  const memoBody = JSON.stringify(body);
  /**
   * Refetches the data from the API with optional overrides for the request body.
   *
   * @param body - (Optional) The new request body to use for the API call.
   * @param override - (Optional) A boolean indicating whether to override the existing request body. Defaults to false.
   * @returns A promise that resolves to the fetched data or undefined.
   */
  const refetch = useCallback(
    async (body?: Record<string, any>, override?: boolean) => {
      setIsLoading(true);
      setError(undefined);

      let mergedBody = {};
      if (memoBody && !body) {
        mergedBody = JSON.parse(memoBody);
      } else if (!memoBody && body) {
        mergedBody = body;
      } else if (memoBody && body && override) {
        mergedBody = { ...JSON.parse(memoBody), ...body };
      } else if (memoBody && body) {
        mergedBody = mergeObjects(JSON.parse(memoBody), body);
      }

      try {
        let result: any = null;
        if (window?.api?.get) {
          result = await window.api.get(request, mergedBody, cache);
        } else {
          const res = await axios.post(
            getApiUrl(),
            JSON.stringify({
              request,
              _app: true,
              ...getApiAuthData(),
              ...mergedBody,
              dto: getDateTimeRecord(),
            }),
          );
          result = res.data;
        }
        if (result.logout) {
          if (window.location.hash.includes("/exports")) {
            return undefined;
          }
          redirectToLogin();
          throw new Error("error in get");
        }

        const apiError = resolveApiError(result, request);
        if (apiError) {
          setError(apiError);
          if (shouldNotify(apiError)) {
            Notification("Error", parseErrorMessage(apiError.message), "red");
          }
          return undefined;
        }

        if (result?.data?.gdpr === 0) {
          dispatch(setShowGDPRModal(true));
        }

        const mappedData = mapper ? mapper(result) : result;
        setData(mappedData);

        return mappedData;
      } catch (error) {
        const message = parseErrorMessage(String(error));
        const apiError: ApiError = {
          code: API_ERROR_CODES.ERROR,
          message,
        };
        setError(apiError);
        Notification("Error", message, "red");
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    },
    [memoBody, mapper, request, cache, dispatch],
  );

  useEffect(() => {
    if (fetchOnInit) {
      void refetch();
    }
  }, [fetchOnInit, refetch]);

  return {
    data,
    setData,
    setError,
    refetch,
    isLoading,
    error,
  };
};
